import { Control, ControlsTreeContext } from "@/types/controls";
import { isObject } from "@/util/objects";
import { addHoverToPath } from "@/lib/controls/utils";
import { getDynamicMetaPath } from "@/lib/dynamic-data/utils";

export const CONTROL_CLEAR_VALUE = null;

export function clear(
  path: string,
  control: Control,
  context: ControlsTreeContext
) {
  if (control.enableHover) {
    // TODO this will trigger additional, redundant undo snapshot.
    // Wrapping it with transaction here is meaningless because stores will
    // create their own transaction when setting property value
    context.setControlValue(addHoverToPath(path), CONTROL_CLEAR_VALUE, control);
  }
  context.setControlValue(path, CONTROL_CLEAR_VALUE, control);

  const dynamicPropertiesEnabled = !!context.dynamicProperty(path);
  if (dynamicPropertiesEnabled) {
    context.setControlValue(
      getDynamicMetaPath(path),
      CONTROL_CLEAR_VALUE,
      control
    );
  }

  /* setting to undefined actually blows up VueSimpleDeepset
    when you then try to set a control value that has previously been cleared
    by this function. so we use null */
}

export function valuePresent(
  path: string,
  control: Control,
  context: ControlsTreeContext
) {
  let hoverPath: string | null = null;
  if (control.enableHover) {
    hoverPath = addHoverToPath(path);
  }

  return (
    doesControlHaveValueSetOrDoesControlSectionHaveDescendantsWithValue(
      context.getControlValue(path)
    ) ||
    (hoverPath &&
      doesControlHaveValueSetOrDoesControlSectionHaveDescendantsWithValue(
        context.getControlValue(hoverPath)
      ))
  );
}

export function isControlValueEmpty(val: unknown) {
  return val === null || val === undefined || val === false;
}

export function doesControlHaveValueSetOrDoesControlSectionHaveDescendantsWithValue(
  controlOrControlSectionValue: unknown
) {
  if (isControlValueEmpty(controlOrControlSectionValue)) {
    return false;
  } else if (isObject(controlOrControlSectionValue)) {
    return deepReduceObject(
      (v, acc) => {
        return [...acc, !isControlValueEmpty(v)];
      },
      controlOrControlSectionValue,
      [] as boolean[]
    ).includes(true);
  } else {
    return true;
  }
}

function deepReduceObject<T>(
  callback: (value: unknown, acc: T) => T,
  object: object,
  init: T
) {
  let acc = init;

  Object.values(object).forEach((value: unknown) => {
    if (isObject(value)) {
      acc = deepReduceObject(callback, value, acc);
    } else {
      acc = callback(value, acc);
    }
  });

  return acc;
}
